import { CurrencyCode } from '@tickeat/types';

/**
 * Configuration des devises supportées
 */
export const CURRENCIES: Record<
  CurrencyCode,
  { code: CurrencyCode; locale: string; symbol: string }
> = {
  EUR: { code: CurrencyCode.EUR, locale: 'fr-FR', symbol: '€' },
  CHF: { code: CurrencyCode.CHF, locale: 'fr-CH', symbol: 'CHF' },
  USD: { code: CurrencyCode.USD, locale: 'en-US', symbol: '$' },
} as const;

export const DEFAULT_CURRENCY: CurrencyCode = CurrencyCode.EUR;

/**
 * Formate un prix en centimes vers une chaîne de caractères formatée
 * selon la devise et la locale associée.
 *
 * @param priceInCents - Le prix en centimes (ex: 1250 pour 12,50€)
 * @param currencyCode - Le code de la devise (EUR, CHF, USD)
 * @returns La chaîne formatée (ex: "12,50 €" ou "CHF 12.50")
 */
export function formatPrice(
  priceInCents: number | null | undefined,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY,
): string {
  const safePrice = typeof priceInCents === 'number' ? priceInCents : 0;
  const currency = CURRENCIES[currencyCode] ?? CURRENCIES[DEFAULT_CURRENCY];

  return new Intl.NumberFormat(currency.locale, {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safePrice / 100);
}

/**
 * Retourne uniquement le symbole de la devise
 */
export function getCurrencySymbol(currencyCode: CurrencyCode = DEFAULT_CURRENCY): string {
  return CURRENCIES[currencyCode]?.symbol ?? CURRENCIES[DEFAULT_CURRENCY].symbol;
}

/**
 * Convertit un prix en centimes vers l'unité monétaire (ex: cents -> euros)
 * @deprecated Préférer formatPrice() pour l'affichage
 */
export function convertToUnit(priceInCents: number | null | undefined): string {
  if (typeof priceInCents !== 'number') {
    return (0).toFixed(2);
  }
  return (priceInCents / 100).toFixed(2);
}

/**
 * Convertit un prix de l'unité monétaire vers les centimes
 */
export function convertToCents(price: number | string): number {
  return Math.round(+price * 100);
}
